import { addMonthsToYearMonth, effectiveRatePeriods, expandRecurringOverpayments, findActiveRate, isDiscountedMonth } from './timeline.js';
import { calcDecliningPrincipalPart, calcFixedPayment, monthlyRate, monthlyRemainingTerm, settleScheduledPayment, settlePaymentWithInterest } from './payments.js';
import { calcCalendarInterest, calendarAnnuity, calendarInterestFloor, calendarPaymentDate, calendarRemainingTerm, initialCalendarPayment, isCalendarDate } from './bank-calendar.js';
import { roundCents } from './rounding.js';
import { calculateAdaptiveOverpayment } from './adaptive-payments.js';
import {
  AdaptiveOverpayment,
  MonthRow,
  Overpayment,
  RefinanceEvent,
  RepaymentType,
  Scenario,
  ScheduleResult,
  StrategyType,
} from './types.js';

/**
 * Thrown when a schedule cannot be computed: the contractual horizon is not a
 * finite positive number of months, a payment or interest amount is not finite,
 * or the month loop runs past the horizon without settling the balance.
 */
export class ScheduleError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ScheduleError';
  }
}

type NormalizedOverpayment = {
  month: number;
  amount: number;
  strategy: StrategyType;
  adaptive?: AdaptiveOverpayment;
};

/**
 * Number of months after which every contractual term (initial or set by a
 * refinance) has ended. The final scheduled month always settles the balance,
 * so a valid scenario never produces a row at or beyond this index.
 */
export function scheduleHorizon(s: Scenario): number {
  return s.refinances.reduce(
    (max, r) => Math.max(max, r.month + r.newTermMonths),
    s.termMonths,
  );
}

export function buildSchedule(s: Scenario): ScheduleResult {
  const calendar = s.calculationMode === 'bank_calendar' ? s.bankCalendar : undefined;
  if (s.calculationMode === 'bank_calendar' && (!calendar || !isCalendarDate(calendar.disbursementDate)
    || !Number.isInteger(calendar.paymentDay) || calendar.paymentDay < 1 || calendar.paymentDay > 31
    || calendarPaymentDate(s.startDate, 0, calendar.paymentDay) <= calendar.disbursementDate)) {
    throw new ScheduleError('Calendar mode requires a valid disbursement date, payment day and first payment after disbursement');
  }
  const horizon = scheduleHorizon(s);
  if (!Number.isFinite(horizon) || horizon <= 0) {
    throw new ScheduleError('Schedule horizon must be a finite positive number of months');
  }
  const overpayments = expandRecurringOverpayments(s.overpayments, horizon);
  const ratePeriods = effectiveRatePeriods(s).sort((a, b) => a.startMonth - b.startMonth);
  // Looked up by month so a refinance is never skipped; with duplicate months
  // (rejected by validateScenario) the last one wins.
  const refinanceByMonth = new Map<number, RefinanceEvent>(s.refinances.map(r => [r.month, r]));
  const overpaymentByMonth = groupOverpayments(overpayments, s.overpaymentStrategy);

  let balance = s.principal;
  let monthIndex = 0;
  let payoffMonth = 0;
  let remainingTerm = s.termMonths;

  let baseRateApr = s.rateApr;
  let repaymentType: RepaymentType = s.repaymentType;
  // Fixed loans keep an annuity payment; declining loans keep a constant
  // principal part. Either is recomputed from the current balance and remaining
  // term at the start, after a refinance, after a reduce_payment overpayment
  // and (fixed only) whenever the active rate changes.
  let fixedPayment = 0;
  let principalPart = 0;
  let lastRateApr: number | undefined;
  let refreshNeeded = true;
  let calendarCheckpointActive = true;
  let previousPaymentDate = calendar?.disbursementDate;

  const months: MonthRow[] = [];

  while (balance > 0) {
    if (monthIndex >= horizon) {
      throw new ScheduleError(`Schedule did not converge within ${horizon} months`);
    }

    // Apply refinancing if present this month (before calculating payment).
    const refinance = refinanceByMonth.get(monthIndex);
    if (refinance) {
      baseRateApr = refinance.newRateApr;
      remainingTerm = refinance.newTermMonths;
      repaymentType = refinance.repaymentType ?? repaymentType;
      refreshNeeded = true;
      calendarCheckpointActive = false; // the original contract's checkpoint no longer applies
    }

    // Discounted or base rate for this month.
    const rateApr = findActiveRate(monthIndex, baseRateApr, ratePeriods);
    const paymentDate = calendar ? calendarPaymentDate(s.startDate, monthIndex, calendar.paymentDay) : undefined;
    const calendarInterest = calendar ? calcCalendarInterest(balance, rateApr, previousPaymentDate!, paymentDate!) : undefined;
    const paymentRecalculated = Boolean(calendar && calendarCheckpointActive
      && calendar.recalculationMonths?.includes(monthIndex));
    if (refreshNeeded || paymentRecalculated || (repaymentType === 'fixed' && rateApr !== lastRateApr)) {
      if (repaymentType === 'fixed') {
        if (!calendar) fixedPayment = calcFixedPayment(balance, remainingTerm, rateApr);
        else if (monthIndex === 0) fixedPayment = initialCalendarPayment(s, calendarInterest!, rateApr);
        else fixedPayment = calendarAnnuity(balance, remainingTerm, rateApr);
      } else {
        principalPart = calcDecliningPrincipalPart(balance, remainingTerm);
      }
      lastRateApr = rateApr;
      refreshNeeded = false;
    }

    const nominalPayment = repaymentType === 'fixed'
      ? fixedPayment
      : principalPart + (calendarInterest ?? roundCents(balance * monthlyRate(rateApr)));
    if (calendar && remainingTerm > 1 && nominalPayment < calendarInterest!) {
      throw new ScheduleError(`Calendar payment does not cover interest in month ${monthIndex}`);
    }
    const settlement = calendar
      ? settlePaymentWithInterest(balance, nominalPayment, calendarInterest!, remainingTerm <= 1)
      : settleScheduledPayment(balance, nominalPayment, rateApr, remainingTerm <= 1);
    const { scheduledPayment, interestPortion, principalPortion } = settlement;
    if (!Number.isFinite(scheduledPayment) || !Number.isFinite(interestPortion)) {
      throw new ScheduleError(`Payment is not finite in month ${monthIndex}`);
    }

    let balanceAfter = settlement.balanceAfter;
    let overpaymentApplied = 0;
    let reducePaymentTriggered = false;
    let reduceTermTriggered = false;
    const monthOverpayments = overpaymentByMonth.get(monthIndex) ?? [];

    // Overpayments apply to the balance left after the scheduled payment, in
    // list order, and are capped so the balance never goes negative.
    for (const entry of monthOverpayments) {
      if (balanceAfter <= 0) break;
      const requested = entry.adaptive
        ? calculateAdaptiveOverpayment(entry.adaptive, { scheduledPayment, balance: balanceAfter })
        : roundCents(entry.amount);
      const applied = Math.min(requested, balanceAfter);
      if (applied <= 0) continue;
      overpaymentApplied += applied;
      const balanceBeforeOverpayment = balanceAfter;
      balanceAfter -= applied;
      if (entry.strategy === 'reduce_payment') {
        if (calendar && repaymentType === 'fixed') {
          // Calendar contract: proportionally scale the retained payment; the reduction starts
          // with the next due date. Rounding must not take it below the 31-day interest floor.
          fixedPayment = Math.max(
            roundCents(fixedPayment * balanceAfter / balanceBeforeOverpayment),
            calendarInterestFloor(balanceAfter, rateApr),
          );
        } else {
          reducePaymentTriggered = true;
        }
      } else {
        reduceTermTriggered = true;
      }
    }
    payoffMonth = monthIndex;

    const dateLabel = addMonthsToYearMonth(s.startDate, monthIndex);

    months.push({
      monthIndex,
      dateLabel,
      ...(paymentDate ? { paymentDate } : {}),
      scheduledPayment,
      overpayment: overpaymentApplied,
      interestPortion,
      principalPortion,
      rateApr,
      balanceAfter,
      events: {
        discountedRate: isDiscountedMonth(monthIndex, ratePeriods),
        overpayment: overpaymentApplied > 0,
        refinance: refinance !== undefined,
        ...(paymentRecalculated ? { paymentRecalculated: true } : {}),
      },
    });

    balance = balanceAfter;
    monthIndex += 1;
    remainingTerm = Math.max(remainingTerm - 1, 0);
    previousPaymentDate = paymentDate;
    // reduce_term keeps the payment (annuity) or the principal part (declining) and shortens
    // the term at the current rate, so a later recalculation (end of a discount, a scheduled
    // checkpoint, reduce_payment) works over the shortened term instead of restoring maturity.
    if (reduceTermTriggered && balance > 0) {
      if (repaymentType === 'fixed') {
        remainingTerm = calendar
          ? calendarRemainingTerm(s, balance, fixedPayment, rateApr, monthIndex, remainingTerm)
          : monthlyRemainingTerm(balance, fixedPayment, rateApr, remainingTerm);
      } else if (principalPart > 0) {
        remainingTerm = Math.min(remainingTerm, Math.ceil(balance / principalPart));
      }
    }

    if (reducePaymentTriggered) {
      refreshNeeded = true;
    }
  }

  return { months, payoffMonth };
}

function groupOverpayments(list: Overpayment[], defaultStrategy: StrategyType): Map<number, NormalizedOverpayment[]> {
  const map = new Map<number, NormalizedOverpayment[]>();
  list.forEach(item => {
    const bucket = map.get(item.month) ?? [];
    bucket.push({
      month: item.month,
      amount: item.amount,
      strategy: item.strategy ?? defaultStrategy,
      adaptive: item.adaptive,
    });
    map.set(item.month, bucket);
  });
  return map;
}

